(function () {
  const instances = new WeakMap();

  class ReconNavigation {
    constructor(el) {
      this.el = el;
      this.desktopShell = el.querySelector('[data-recon-nav-shell]');
      this.mobileNav = el.querySelector('[data-recon-mobile-nav]');
      this.mobileUtilityTarget = this.mobileNav
        ? this.mobileNav.querySelector('[data-recon-mobile-utility-target]')
        : null;
      this.activeTrigger = null;
      this.activePanel = null;
      this.activeName = null;
      this.closeTimer = null;
      this.mobileObserver = null;
      this.mobileTriggers = [];
      this.mobileContents = [];

      this._bindDesktop();
      this._bindMobile();
    }

    destroy() {
      this._clearDesktopCloseTimer();

      if (this.desktopShell) {
        this.desktopShell.removeEventListener('mouseleave', this.onDesktopMouseLeave);
        this.desktopShell.removeEventListener('mouseenter', this.onDesktopMouseEnter);
      }

      if (this.desktopButtons) {
        this.desktopButtons.forEach(button => {
          button.removeEventListener('mouseenter', button._reconMouseEnter);
          button.removeEventListener('focus', button._reconFocus);
          button.removeEventListener('click', button._reconClick);
        });
      }

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('focusin', this.onDocumentFocus);
      document.removeEventListener('keydown', this.onDocumentKeydown);

      this.mobileTriggers.forEach(trigger => {
        trigger.removeEventListener('click', trigger._reconClick);
      });

      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
      }
    }

    _bindDesktop() {
      if (!this.desktopShell) {
        return;
      }

      this.desktopButtons = Array.from(this.desktopShell.querySelectorAll('[data-recon-nav-trigger]'));

      if (!this.desktopButtons.length) {
        return;
      }

      this.onDesktopMouseLeave = () => this._scheduleDesktopClose();
      this.onDesktopMouseEnter = () => this._clearDesktopCloseTimer();
      this.onDocumentClick = event => {
        if (!this.activeName || this.desktopShell.contains(event.target)) {
          return;
        }

        this._closeDesktop();
      };
      this.onDocumentFocus = event => {
        if (!this.activeName || this.desktopShell.contains(event.target)) {
          return;
        }

        this._closeDesktop();
      };
      this.onDocumentKeydown = event => {
        if (event.key !== 'Escape' || !this.activeTrigger) {
          return;
        }

        const trigger = this.activeTrigger;
        this._closeDesktop();
        trigger.focus();
      };

      this.desktopButtons.forEach(button => {
        const name = button.getAttribute('data-recon-nav-trigger');

        button._reconMouseEnter = () => this._openDesktop(name);
        button._reconFocus = () => this._openDesktop(name);
        button._reconClick = event => {
          event.preventDefault();

          if (this.activeName === name) {
            this._closeDesktop();
            return;
          }

          this._openDesktop(name);
        };

        button.addEventListener('mouseenter', button._reconMouseEnter);
        button.addEventListener('focus', button._reconFocus);
        button.addEventListener('click', button._reconClick);
      });

      this.desktopShell.addEventListener('mouseleave', this.onDesktopMouseLeave);
      this.desktopShell.addEventListener('mouseenter', this.onDesktopMouseEnter);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('focusin', this.onDocumentFocus);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }

    _openDesktop(name) {
      const trigger = this.desktopShell.querySelector('[data-recon-nav-trigger="' + name + '"]');
      const panel = this.desktopShell.querySelector('[data-recon-nav-panel="' + name + '"]');

      if (!trigger || !panel) {
        return;
      }

      this._clearDesktopCloseTimer();

      if (this.activePanel && this.activePanel !== panel) {
        this._closeDesktop();
      }

      this.activeName = name;
      this.activeTrigger = trigger;
      this.activePanel = panel;

      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.classList.add('is-open');
    }

    _closeDesktop() {
      if (!this.activePanel || !this.activeTrigger) {
        this.activeName = null;
        return;
      }

      this.activeTrigger.classList.remove('is-active');
      this.activeTrigger.setAttribute('aria-expanded', 'false');
      this.activePanel.classList.remove('is-open');
      this.activePanel.hidden = true;

      this.activeTrigger = null;
      this.activePanel = null;
      this.activeName = null;
    }

    _scheduleDesktopClose() {
      this._clearDesktopCloseTimer();
      this.closeTimer = window.setTimeout(() => this._closeDesktop(), 150);
    }

    _clearDesktopCloseTimer() {
      if (!this.closeTimer) {
        return;
      }

      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    _bindMobile() {
      if (!this.mobileNav) {
        return;
      }

      this._syncMobileUtilityLinks();
      this.mobileTriggers = Array.from(this.mobileNav.querySelectorAll('[data-recon-mobile-trigger]'));
      this.mobileContents = Array.from(this.mobileNav.querySelectorAll('.recon-mobile-nav__content'));

      this.mobileTriggers.forEach(trigger => {
        trigger._reconClick = () => this._toggleMobileSection(trigger);
        trigger.addEventListener('click', trigger._reconClick);
      });

      if ('MutationObserver' in window) {
        this.mobileObserver = new MutationObserver(() => {
          if (this.mobileNav.getAttribute('data-open') === 'true') {
            this._syncMobileUtilityLinks();
            return;
          }

          this._collapseMobileSections();
        });

        this.mobileObserver.observe(this.mobileNav, {
          attributes: true,
          attributeFilter: ['data-open']
        });
      }
    }

    _syncMobileUtilityLinks() {
      if (!this.mobileUtilityTarget) {
        return;
      }

      const source = document.querySelector('[data-recon-mobile-utility-source]');
      this.mobileUtilityTarget.innerHTML = '';

      if (!source) {
        this.mobileUtilityTarget.hidden = true;
        return;
      }

      const sourceContent = source.content ? source.content.cloneNode(true) : source.cloneNode(true);
      const links = Array.from(sourceContent.querySelectorAll('a'));

      if (!links.length) {
        this.mobileUtilityTarget.hidden = true;
        return;
      }

      links.forEach(link => {
        link.className = 'recon-mobile-nav__secondary-link recon-mobile-nav__secondary-link--utility';
      });

      this.mobileUtilityTarget.appendChild(sourceContent);
      this.mobileUtilityTarget.hidden = false;
    }

    _toggleMobileSection(trigger) {
      const contentId = trigger.getAttribute('aria-controls');
      const content = contentId ? this.mobileNav.querySelector('#' + contentId) : null;
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      this._collapseMobileSections();

      if (isExpanded || !content) {
        return;
      }

      trigger.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    }

    _collapseMobileSections() {
      this.mobileTriggers.forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.mobileContents.forEach(content => {
        content.hidden = true;
      });
    }
  }

  function init(root) {
    const elements = [];

    if (root.matches && root.matches('[data-recon-navigation]')) {
      elements.push(root);
    }

    root.querySelectorAll('[data-recon-navigation]').forEach(el => {
      elements.push(el);
    });

    elements.forEach(el => {
      if (instances.has(el)) {
        return;
      }

      instances.set(el, new ReconNavigation(el));
    });
  }

  function destroy(root) {
    const elements = [];

    if (root.matches && root.matches('[data-recon-navigation]')) {
      elements.push(root);
    }

    root.querySelectorAll('[data-recon-navigation]').forEach(el => {
      elements.push(el);
    });

    elements.forEach(el => {
      const instance = instances.get(el);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() {
      document.removeEventListener('DOMContentLoaded', onReady);
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', event => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    destroy(event.target);
  });
})();
